import apiClient from './api-client';

// API Path Constants
const API_BASE = '/masterdata/data-elements';
const API_PATHS = {
    BASE: API_BASE,
    BY_ID: (id: number) => `${API_BASE}/${id}`,
    BY_CODE: (code: string) => `${API_BASE}/code/${code}`,
    TYPES: `${API_BASE}/types`,
    IDENTIFIERS: `${API_BASE}/identifiers`,
} as const;

export type DataElementType = {
    TypeId: number;
    TypeName: string;
    Description?: string;
    CreatedDate: string;
    ModifiedDate?: string | null;
};

export interface SelectionOption {
    optionId?: number;
    elementId: number;
    optionValue: string;
    optionLabel: string;
    displayOrder: number;
    isEnabled: boolean;
}

export interface DataElement {
    elementId: number;
    clientId: number;
    elementName: string;
    elementCode: string;
    description?: string;
    typeId: number;
    isIdentifier: boolean;
    isPersonalData: boolean;
    validationRule?: string;
    isMultiSelect: boolean;
    isPublished: boolean;
    options?: SelectionOption[];
    createdDate?: string;
    modifiedDate?: string;
    type?: {
        typeId: number;
        typeName: string;
        description?: string;
        createdDate: string;
        modifiedDate?: string;
    };
    isActive: boolean;
}

// Add a type for creating new data elements
export type CreateDataElement = Omit<DataElement, 'elementId'>;

export interface PaginatedResponse<T> {
    elements: T[];
    total: number;
}

export interface PaginationParams {
    page: number;
    pageSize: number;
}

export interface DataElementOptionTranslation {
    optionTranslationId: number;
    optionId: number;
    translatedLabel: string;
    createdBy?: number;
    createdDate?: Date;
    modifiedBy?: number;
    modifiedDate?: Date;
}

export interface NewOptionTranslation {
    optionId: number;
    translatedLabel: string;
}

export interface DataElementTranslation {
    translationId: number;
    elementId: number;
    languageCode: string;
    translatedName: string;
    translatedDescription?: string;
    createdBy?: number;
    createdDate?: Date;
    modifiedBy?: number;
    modifiedDate?: Date;
    optionTranslations?: DataElementOptionTranslation[];
}

// Service methods
export const dataElementsService = {
    getDataElements: async (
        filters?: {
            typeId?: number;
            isIdentifier?: boolean;
            isPublished?: boolean;
            isPersonalData?: boolean;
        },
        pagination?: PaginationParams,
        sortBy?: string
    ): Promise<PaginatedResponse<DataElement>> => {
        const queryParams = new URLSearchParams();
        
        if (filters?.typeId) {
            queryParams.append('typeId', filters.typeId.toString());
        }
        if (filters?.isIdentifier !== undefined) {
            queryParams.append('isIdentifier', filters.isIdentifier.toString());
        }
        if (filters?.isPublished !== undefined) {
            queryParams.append('isPublished', filters.isPublished.toString());
        }
        if (filters?.isPersonalData !== undefined) {
            queryParams.append('isPersonalData', filters.isPersonalData.toString());
        }
        if (pagination) {
            queryParams.append('page', pagination.page.toString());
            queryParams.append('pageSize', pagination.pageSize.toString());
        }
        if (sortBy) {
            queryParams.append('sortBy', sortBy);
        }

        const response = await apiClient.get<{ 
            success: boolean; 
            elements: DataElement[]; 
            total: number;
        }>(`${API_PATHS.BASE}?${queryParams.toString()}`);
        
        return {
            elements: response.elements,
            total: response.total
        };
    },

    getDataElementById: async (id: number): Promise<DataElement | undefined> => {
        const response = await apiClient.get<{ success: boolean; data: DataElement }>(API_PATHS.BY_ID(id));
        return response.data;
    },

    getDataElementByCode: async (code: string): Promise<DataElement | undefined> => {
        const response = await apiClient.get<{ success: boolean; data: DataElement }>(API_PATHS.BY_CODE(code));
        return response.data;
    },

    getIdentifierDataElements: async (): Promise<DataElement[]> => {
        const response = await apiClient.get<{ success: boolean; data: DataElement[] }>(API_PATHS.IDENTIFIERS);
        return response.data;
    },

    getDataElementTypes: async (): Promise<DataElementType[]> => {
        const response = await apiClient.get<DataElementType[]>(API_PATHS.TYPES);
        return response;
    },

    createDataElement: async (element: CreateDataElement): Promise<DataElement> => {
        const response = await apiClient.post<{ success: boolean; data: DataElement }>(API_PATHS.BASE, element);
        return response.data;
    },

    updateDataElement: async (elementId: number, updates: Partial<DataElement>): Promise<PaginatedResponse<DataElement>> => {
        const response = await apiClient.put<PaginatedResponse<DataElement>>(API_PATHS.BY_ID(elementId), updates);
        return response;
    },

    deleteDataElement: async (elementId: number): Promise<void> => {
        await apiClient.delete<{ success: boolean; message: string }>(API_PATHS.BY_ID(elementId));
    },

    updatePublishStatus: async (elementId: number, isPublished: boolean): Promise<DataElement> => {
        const response = await apiClient.patch<{ success: boolean; data: DataElement }>(
            `${API_PATHS.BY_ID(elementId)}/publish`,
            { isPublished }
        );
        return response.data;
    },

    getDataElementTranslations: async (elementId: number): Promise<DataElementTranslation[]> => {
        const response = await apiClient.get<DataElementTranslation[]>(
            `${API_PATHS.BY_ID(elementId)}/translations`
        );
        return response.map(t => ({
            ...t,
            optionTranslations: Array.isArray(t.optionTranslations) ? t.optionTranslations : []
        }));
    },

    updateDataElementTranslation: async (
        elementId: number,
        languageCode: string,
        translatedName: string,
        translatedDescription?: string,
        optionTranslations?: NewOptionTranslation[]
    ): Promise<DataElementTranslation> => {
        const response = await apiClient.put<{ success: boolean; data: DataElementTranslation }>(
            `${API_PATHS.BY_ID(elementId)}/translations`,
            {
                elementId,
                languageCode,
                translatedName,
                translatedDescription,
                optionTranslations
            }
        );
        return response.data;
    },

    getDataElementOptions: async (elementId: number): Promise<SelectionOption[]> => {
        const response = await apiClient.get<{ success: boolean; data: SelectionOption[] }>(
            `${API_PATHS.BY_ID(elementId)}`
        );
        return response.data;
    },

    updateDataElementOptions: async (elementId: number, options: SelectionOption[]): Promise<DataElement> => {
        const response = await apiClient.put<DataElement>(
            `${API_PATHS.BY_ID(elementId)}/options`,
            { options }
        );
        return response;
    },

    updateDataElementOptionTranslation: async (
        elementId: number,
        optionId: number,
        languageCode: string,
        translatedLabel: string
    ): Promise<void> => {
        await apiClient.put<{ success: boolean }>(
            `${API_PATHS.BY_ID(elementId)}/options/${optionId}/translations`,
            {
                elementId,
                optionId,
                languageCode,
                translatedLabel
            }
        );
    }
}; 